import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Scale, Download, Loader2, Building2,
  ArrowDownCircle, ArrowUpCircle, Info,
} from 'lucide-react';

const fmtUsd = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

export default function FinanceReportTab() {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/finance/orgs').then((r) => setOrgs(r.data?.orgs || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (orgId && orgId !== 'all') params.org_id = orgId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get('/finance/report', { params });
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load finance report');
    } finally {
      setLoading(false);
    }
  }, [orgId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (orgId && orgId !== 'all') params.org_id = orgId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/finance/report/export.csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance_credit_debit_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('CSV export failed');
    }
  };

  const mi = report?.money_in || {};
  const mo = report?.money_out || {};
  const net = report?.net || 0;
  const monthly = report?.monthly || [];
  const recent = report?.recent || [];
  const maxBar = Math.max(1, ...monthly.map((m) => Math.max(m.in, m.out)));

  return (
    <div className="space-y-6" data-testid="finance-report-tab">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Finance — Stripe Credit / Debit
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Money IN (paid credit top-ups + subscription payments) vs money OUT (executed refunds).
          </p>
        </div>
        <Button variant="outline" className="rounded-none" onClick={exportCsv} data-testid="finance-export-csv">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap border border-border p-4 bg-card">
        <div className="min-w-[220px]">
          <Label className="text-xs">Organisation</Label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="rounded-none" data-testid="finance-org-filter">
              <SelectValue placeholder="All organisations" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All organisations</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.account_id} value={o.account_id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 className="rounded-none" data-testid="finance-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 className="rounded-none" data-testid="finance-date-to" />
        </div>
        <Button className="rounded-none" onClick={load} data-testid="finance-apply-filters">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Apply
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-border p-5 bg-card" data-testid="finance-kpi-in">
          <div className="flex items-center gap-2 text-emerald-600">
            <TrendingUp className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">Money In</span>
          </div>
          <div className="text-3xl font-black mt-2">{fmtUsd(mi.total)}</div>
          <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
            <div>Top-ups: <span className="font-medium text-foreground">{fmtUsd(mi.topups)}</span> ({mi.topups_count || 0})</div>
            <div>Subscriptions: <span className="font-medium text-foreground">{fmtUsd(mi.subscriptions)}</span> ({mi.subscriptions_count || 0})</div>
          </div>
        </div>
        <div className="border border-border p-5 bg-card" data-testid="finance-kpi-out">
          <div className="flex items-center gap-2 text-rose-600">
            <TrendingDown className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">Money Out</span>
          </div>
          <div className="text-3xl font-black mt-2">{fmtUsd(mo.total)}</div>
          <div className="text-xs text-muted-foreground mt-2">Executed refunds: {mo.count || 0}</div>
        </div>
        <div className="border border-border p-5 bg-card" data-testid="finance-kpi-net">
          <div className="flex items-center gap-2 text-foreground">
            <Scale className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">Net</span>
          </div>
          <div className={`text-3xl font-black mt-2 ${net < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
            {fmtUsd(net)}
          </div>
          <div className="text-xs text-muted-foreground mt-2">In − Out</div>
        </div>
      </div>

      {!report?.live_stripe && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground border border-dashed border-border p-3 bg-muted/30">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            Sourced from platform payment records (<code>payment_transactions</code> + finance refunds).
            The live-Stripe path activates automatically once a real Stripe key is configured on production.
          </span>
        </div>
      )}

      {/* Monthly breakdown */}
      <div className="border border-border bg-card">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm">Monthly breakdown</div>
        {monthly.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">No movements in this period.</div>
        ) : (
          <div className="p-4 space-y-3" data-testid="finance-monthly">
            {monthly.map((m) => (
              <div key={m.month} className="grid grid-cols-[70px_1fr_auto] items-center gap-3">
                <span className="text-xs font-mono text-muted-foreground">{m.month}</span>
                <div className="flex flex-col gap-1">
                  <div className="h-2.5 bg-emerald-500" style={{ width: `${(m.in / maxBar) * 100}%`, minWidth: m.in > 0 ? '4px' : 0 }} />
                  <div className="h-2.5 bg-rose-500" style={{ width: `${(m.out / maxBar) * 100}%`, minWidth: m.out > 0 ? '4px' : 0 }} />
                </div>
                <span className={`text-xs font-semibold tabular-nums ${m.net < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {fmtUsd(m.net)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent movements */}
      <div className="border border-border bg-card">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm">Recent movements</div>
        {recent.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">No recent movements.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="finance-recent-table">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Organisation</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={`${r.direction}-${r.id}`} className="border-b border-border/60">
                    <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1.5 ${r.direction === 'in' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {r.direction === 'in'
                          ? <ArrowDownCircle className="h-3.5 w-3.5" />
                          : <ArrowUpCircle className="h-3.5 w-3.5" />}
                        {r.kind}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        {r.org_name || '—'}
                      </span>
                    </td>
                    <td className={`px-4 py-2 text-right font-semibold tabular-nums ${r.direction === 'in' ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {r.direction === 'in' ? '+' : '−'}{fmtUsd(r.amount_usd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
